import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Calendar, Trophy, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import TournamentForm from '../components/TournamentForm';

const Tournaments = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [filter, setFilter] = useState({ search: '', status: '' });
  const [sorting, setSorting] = useState({ sort_by: 'start_date', sort_order: 'desc' });
  const queryClient = useQueryClient();

  const { data: tournaments = [], isLoading } = useQuery({
    queryKey: ['tournaments', filter, sorting],
    queryFn: () => {
      const cleanFilter = Object.fromEntries(
        Object.entries({ ...filter, ...sorting }).filter(([_, value]) => value && value.toString().trim() !== '')
      );
      return api.getTournaments(cleanFilter);
    }
  });

  const createMutation = useMutation({
    mutationFn: api.createTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      setShowForm(false);
      toast.success('Tournament created successfully!');
    },
    onError: () => toast.error('Failed to create tournament'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updateTournament(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      setShowForm(false);
      setEditingTournament(null);
      toast.success('Tournament updated successfully!');
    },
    onError: () => toast.error('Failed to update tournament'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      toast.success('Tournament deleted successfully!');
    },
    onError: () => toast.error('Failed to delete tournament'),
  });

  const handleSubmit = (data) => {
    // Clean the data - convert empty strings to null and remove empty fields
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' || value === null ? null : value
      ]).filter(([_, value]) => value !== null)
    );
    
    if (editingTournament) {
      updateMutation.mutate({ id: editingTournament.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleEdit = (tournament) => {
    setEditingTournament(tournament);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this tournament?')) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      upcoming: 'bg-blue-100 text-blue-800',
      ongoing: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Tournaments</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-cricket-green text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          <span>Create Tournament</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Search tournaments..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sorting.sort_by}
              onChange={(e) => setSorting({ ...sorting, sort_by: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="start_date">Start Date</option>
              <option value="end_date">End Date</option>
              <option value="name">Name</option>
              <option value="created_at">Date Added</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={sorting.sort_order}
              onChange={(e) => setSorting({ ...sorting, sort_order: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {showForm ? (
        <TournamentForm
          tournament={editingTournament}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingTournament(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament) => (
          <div key={tournament.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-cricket-gold rounded-full flex items-center justify-center">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{tournament.name}</h3>
                  <p className="text-sm text-gray-600">{tournament.format}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(tournament)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(tournament.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {tournament.description && (
              <p className="text-gray-600 mb-4 text-sm">{tournament.description}</p>
            )}
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tournament.status)}`}>
                  {tournament.status.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Start Date:</span>
                <span className="text-sm font-medium">{formatDate(tournament.start_date)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">End Date:</span>
                <span className="text-sm font-medium">{formatDate(tournament.end_date)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Venue:</span>
                <span className="text-sm font-medium">{tournament.venue}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Teams:</span>
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">{tournament.teams?.length || 0}</span>
                </div>
              </div>
            </div>

            {tournament.prize_money && (
              <div className="bg-cricket-gold bg-opacity-10 rounded-lg p-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-cricket-gold">
                    ₹{tournament.prize_money.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Prize Money</div>
                </div>
              </div>
            )}
          </div>
        ))}
        </div>
      )}

      {!showForm && tournaments.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No tournaments found</h3>
          <p className="text-gray-500 mb-4">Create your first tournament to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-cricket-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
          >
            Create Tournament
          </button>
        </div>
      )}
    </div>
  );
};

export default Tournaments;
